import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { useBranding } from "./Layout";

const DISMISS_KEY = "sellerhub_pwa_dismissed";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/Chrome|CriOS|FxiOS/i.test(ua);
}

export default function InstallPrompt() {
  const branding = useBranding();
  const [prompt, setPrompt] = useState(null); // beforeinstallprompt event (Chrome/Android)
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed, nothing to do
    // Suppress on the public marketing pages — the PWA install nudge is for
    // signed-in sellers, not random visitors browsing the SaaS pitch.
    try {
      const path = window.location?.pathname || "";
      const host = window.location?.hostname || "";
      const onMarketing = path === "/for-agents"
        || (path === "/" && (host === "theporchlight.io" || host === "www.theporchlight.io"));
      if (onMarketing) return;
    } catch {}
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {}

    const onBip = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS path: show the hint after a small delay so it doesn't overwhelm first load
    if (isIOS()) {
      const t = setTimeout(() => setShowIOSHint(true), 2000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBip);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    setPrompt(null);
    setShowIOSHint(false);
  };

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    try {
      await prompt.userChoice;
    } catch {}
    setPrompt(null);
  };

  if (!prompt && !showIOSHint) return null;

  const baseStyle = {
    position: "fixed",
    left: 16,
    right: 16,
    bottom: 16,
    zIndex: 60,
    maxWidth: 460,
    margin: "0 auto",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    boxShadow: "0 20px 44px rgba(0,0,0,0.18)",
    padding: 16,
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  };

  if (prompt) {
    return (
      <div data-testid="pwa-install-banner" style={baseStyle}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: "var(--primary)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Download size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Add {branding.product_name} to your home screen</div>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 2 }}>One-tap access. Opens full-screen like a regular app.</div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button data-testid="pwa-install-btn" className="btn-primary" style={{ padding: "8px 14px", fontSize: 13 }} onClick={install}>
              Install
            </button>
            <button className="btn-ghost" style={{ padding: "8px 10px", fontSize: 13 }} onClick={dismiss}>Not now</button>
          </div>
        </div>
        <button aria-label="Dismiss" data-testid="pwa-dismiss-btn" onClick={dismiss} style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4 }}>
          <X size={16} />
        </button>
      </div>
    );
  }

  // iOS hint — Safari can't trigger install programmatically, so we explain the steps.
  return (
    <div data-testid="pwa-install-banner-ios" style={baseStyle}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: "var(--primary)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Share size={18} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>Install on iPhone</div>
        <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 2, lineHeight: 1.45 }}>
          Tap <b style={{ color: "var(--text)" }}>Share</b> in Safari, then <b style={{ color: "var(--text)" }}>Add to Home Screen</b>. {branding.product_name} opens full-screen, like a real app.
        </div>
      </div>
      <button aria-label="Dismiss" data-testid="pwa-dismiss-ios-btn" onClick={dismiss} style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4 }}>
        <X size={16} />
      </button>
    </div>
  );
}
